// Pure helpers for arrangement file handling: patient-ID extraction/consistency
// and Azure-folder file classification. Kept DOM-free so they can be unit tested
// in isolation (see src/__tests__/arrangementFiles.test.js).

// Reduce an upload path or file name to its base name (drop any directory or
// azure:// prefix), so patient-ID extraction works for both file uploads and
// Azure blob paths.
function arrangementBaseName(pathOrName) {
    if (!pathOrName) {
        return "";
    }
    const trimmed = String(pathOrName).trim();
    const lastSlash = Math.max(
        trimmed.lastIndexOf("/"),
        trimmed.lastIndexOf("\\")
    );
    return lastSlash >= 0 ? trimmed.slice(lastSlash + 1) : trimmed;
}

// Patient ID convention: the leading identifier ends at the first space, or
// at a "-<digit>" suffix if that comes first - whichever occurs earlier.
// The "-<digit>" form covers per-export/variant suffixes that are NOT part
// of the patient identity (e.g. "-0"/"-1" file-revision markers seen on both
// internal tenant/case-id exports, "1234567890123-0 Obj3D L.obj", and native
// OnyxCeph3 exports with no space before the suffix at all,
// "1234567890123-0_OnyxCeph3_Export Lower.obj") - two files for the same
// case can legitimately differ only in that suffix and must still match.
// Returns "" when the name has no token.
function extractPatientId(pathOrName) {
    const base = arrangementBaseName(pathOrName);
    if (!base) {
        return "";
    }
    const spaceIndex = base.indexOf(" ");
    const dashDigitMatch = base.match(/-\d/);
    const dashIndex = dashDigitMatch ? dashDigitMatch.index : -1;

    let endIndex;
    if (dashIndex === -1) {
        endIndex = spaceIndex;
    } else if (spaceIndex === -1) {
        endIndex = dashIndex;
    } else {
        endIndex = Math.min(spaceIndex, dashIndex);
    }

    const id = endIndex === -1 ? base : base.slice(0, endIndex);
    return id.trim();
}

// Verify that all provided arrangement file names share the same patient ID.
// `entries` is a list of {label, name} for the files actually supplied.
// Returns an error string when the IDs disagree or any ID is empty, else null.
function validatePatientIdConsistency(entries) {
    const present = entries.filter((e) => e.name);
    if (present.length < 2) {
        return null;
    }
    const ids = present.map((e) => ({
        label: e.label,
        id: extractPatientId(e.name)
    }));
    const missing = ids.filter((e) => !e.id);
    if (missing.length > 0) {
        return (
            "Could not determine patient ID from file name(s): " +
            missing.map((e) => e.label).join(", ") +
            ". Expected names like '<patient_id> <role>'."
        );
    }
    const reference = ids[0].id;
    const mismatched = ids.filter((e) => e.id !== reference);
    if (mismatched.length > 0) {
        const summary = ids.map((e) => `${e.label}: ${e.id}`).join(", ");
        return (
            "Patient ID mismatch across files (" +
            summary +
            "). All files must belong to the same patient."
        );
    }
    return null;
}

// Naming convention used to resolve arrangement files from a single Azure folder.
// Configurable so it can be adjusted without touching the matcher:
//   - Jaw .obj files are classified by the LAST name token (case-insensitive):
//     "U"/"Upper" -> upper, "L"/"Lower" -> lower. Onyxceph exports look like
//     "... Obj3D L.obj".
//   - `excludeObj`: .obj names containing any of these substrings are ignored.
//     "step" = post-treatment setups (not the arrangement input); "simplified" =
//     decimated preview meshes.
//   - Info is the .json whose name contains "info"; when several match, the file
//     named exactly "Info.json" wins (`preferExact`) - e.g. over "Modified Info.json".
//   - Landmarks is the .csv whose name contains "landmarks" (not "Wire 3D" /
//     "Protocol Distances").
//   - `contains`/`preferExact` are matched case-insensitively; set `contains` to
//     "" to match by extension alone.
// NOTE: this classifier does not itself check patient-ID consistency - it only
// resolves each role by name convention. The resolved azure:// paths are
// written into the same per-file fields the submit path already validates
// (see collectArrangementInputs()'s validatePatientIdConsistency call), so a
// folder with mismatched patient IDs across roles is still caught there.
const ARRANGEMENT_FOLDER_MATCH_CONFIG = {
    upper: { ext: ".obj", tokens: ["u", "upper"] },
    lower: { ext: ".obj", tokens: ["l", "lower"] },
    info: { ext: ".json", contains: "info", preferExact: "info.json" },
    landmarks: { ext: ".csv", contains: "landmarks" },
    excludeObj: ["step", "simplified"]
};

// True when a file name matches a keyword-based role (info/landmarks): the
// extension matches and, if a keyword is configured, the name contains it.
function matchesKeywordRole(nameLower, roleConfig) {
    if (!nameLower.endsWith(roleConfig.ext)) {
        return false;
    }
    if (roleConfig.contains && !nameLower.includes(roleConfig.contains)) {
        return false;
    }
    return true;
}

// Determine the jaw role of an .obj file by its last name token. Splitting on
// spaces, underscores and hyphens handles both "... Obj3D L.obj" and
// "..._upper.obj" (internal) conventions. Matching the whole last token (not a
// substring) avoids "Upper" being read as lower via the "l" in it, and avoids a
// patient-id fragment being mistaken for a role. Returns "upper"/"lower"/null.
function objJawRole(nameWithoutExt, config) {
    const tokens = nameWithoutExt
        .toLowerCase()
        .split(/[\s_-]+/)
        .filter(Boolean);
    if (tokens.length === 0) {
        return null;
    }
    const last = tokens[tokens.length - 1];
    if (config.upper.tokens.includes(last)) {
        return "upper";
    }
    if (config.lower.tokens.includes(last)) {
        return "lower";
    }
    return null;
}

// Pick the single resolved candidate for a role, or null. When several match,
// `preferExact` (an exact case-insensitive basename) breaks the tie if exactly
// one candidate matches it; otherwise the role stays unresolved (ambiguous).
function pickCandidate(list, preferExact) {
    if (list.length <= 1) {
        return list[0] || null;
    }
    if (preferExact) {
        const exact = list.filter(
            (e) => e.name.toLowerCase() === preferExact.toLowerCase()
        );
        if (exact.length === 1) {
            return exact[0];
        }
    }
    return null; // still ambiguous
}

// Classify the blobs of a single arrangement folder into the four expected roles.
// `blobs` is the endpoint's files array: [{ name, reference, ... }].
// Pure function - never throws. Returns:
//   {
//     resolved: { lower, upper, info, landmarks },  // azure:// reference or null
//     errors:   [string],                            // block submit when non-empty
//     unmatched: [string]                            // informational (ignored files)
//   }
// Robustness rules:
//   - required jaw (.obj) with 0 matches   -> error
//   - a role matched by >1 file that a tie-breaker cannot resolve -> ambiguity error
//   - optional file (info/landmarks) with 0 matches -> null, no error
// Patient-ID is NOT checked (folder = single case boundary; see config note).
function classifyArrangementFolderFiles(
    blobs,
    config = ARRANGEMENT_FOLDER_MATCH_CONFIG
) {
    const candidates = { upper: [], lower: [], info: [], landmarks: [] };
    const unmatched = [];
    const excludeObj = config.excludeObj || [];

    for (const blob of blobs || []) {
        const name = blob && blob.name ? String(blob.name) : "";
        if (!name) {
            continue;
        }
        const nameLower = name.toLowerCase();
        const entry = { name, reference: blob.reference };

        // Branch by extension first so info/landmarks can never be read as a jaw
        // obj and a jaw obj can never be read as info/landmarks. Info/landmarks
        // additionally require their keyword so unrelated extra files are ignored.
        if (matchesKeywordRole(nameLower, config.info)) {
            candidates.info.push(entry);
        } else if (matchesKeywordRole(nameLower, config.landmarks)) {
            candidates.landmarks.push(entry);
        } else if (nameLower.endsWith(config.upper.ext)) {
            // upper.ext and lower.ext are both ".obj". Skip excluded obj variants
            // (post-treatment "step" setups, "simplified" preview meshes).
            if (excludeObj.some((term) => nameLower.includes(term))) {
                unmatched.push(name);
                continue;
            }
            const nameWithoutExt = name.slice(0, -config.upper.ext.length);
            const role = objJawRole(nameWithoutExt, config);
            if (role) {
                candidates[role].push(entry);
            } else {
                unmatched.push(name);
            }
        } else {
            unmatched.push(name);
        }
    }

    const roleLabels = {
        upper: "upper jaw (.obj)",
        lower: "lower jaw (.obj)",
        info: "info.json",
        landmarks: "landmarks (.csv)"
    };

    // Resolve each role to a single file (with tie-breakers where configured).
    const picked = {
        lower: pickCandidate(candidates.lower),
        upper: pickCandidate(candidates.upper),
        info: pickCandidate(candidates.info, config.info.preferExact),
        landmarks: pickCandidate(
            candidates.landmarks,
            config.landmarks.preferExact
        )
    };

    const errors = [];
    // Required jaw files must resolve to exactly one file.
    for (const role of ["lower", "upper"]) {
        if (candidates[role].length === 0) {
            errors.push(`No ${roleLabels[role]} file found in folder`);
        }
    }
    // A role with multiple candidates that no tie-breaker resolved is ambiguous.
    for (const role of ["lower", "upper", "info", "landmarks"]) {
        if (candidates[role].length > 1 && !picked[role]) {
            const names = candidates[role].map((e) => e.name).join(", ");
            errors.push(
                `Ambiguous ${roleLabels[role]}: multiple files matched (${names})`
            );
        }
    }

    const resolved = {
        lower: picked.lower ? picked.lower.reference : null,
        upper: picked.upper ? picked.upper.reference : null,
        info: picked.info ? picked.info.reference : null,
        landmarks: picked.landmarks ? picked.landmarks.reference : null
    };

    return { resolved, errors, unmatched };
}

export {
    arrangementBaseName,
    extractPatientId,
    validatePatientIdConsistency,
    ARRANGEMENT_FOLDER_MATCH_CONFIG,
    classifyArrangementFolderFiles
};
